/**
 * FE mirror of BE services/lds_updated_order_total_ssot baggage guard.
 * When room PWCCF already equals priceWithBaggageFare / customer_total / paid, do not add supplement again.
 */
import { extractLdsOrderFinalPriceIls } from "../extractLdsOrderFinalPriceIls.js";
import { resolveLdsConfirmedPaidSumFromPayments } from "./ldsOrderPaymentSummarySsot.js";

export const LDS_BAGGAGE_DOUBLE_COUNT_GUARD_TRACE =
  "[LDS_BAGGAGE_DOUBLE_COUNT_GUARD_TRACE]";

const MONEY_TOL = 0.02;

function toNumberOrNull(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function positive(value) {
  const n = toNumberOrNull(value);
  return n != null && n > 0 ? n : null;
}

export function round2(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

export function roughlyEqual(a, b, eps = MONEY_TOL) {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  return Math.abs(a - b) <= eps;
}

export function pickSupplementTotal(order) {
  const rootSupp =
    order?.supplement ??
    order?.flights?.supplement ??
    order?.flights?.ourPrice?.supplement;
  if (!rootSupp || typeof rootSupp !== "object") return null;
  let sum = 0;
  let any = false;
  const entries = Array.isArray(rootSupp) ? rootSupp : Object.values(rootSupp);
  for (const entry of entries) {
    if (!entry || typeof entry !== "object") continue;
    const checked =
      entry.checked === true ||
      entry.selected === true ||
      String(entry?.checked || "").trim().toLowerCase() === "true";
    if (!checked) continue;
    const fare = toNumberOrNull(entry.ourFare ?? entry.fare);
    if (fare != null) {
      sum += fare;
      any = true;
    }
  }
  return any ? round2(sum) : null;
}

function readRoomPwccIls(room) {
  if (!room || typeof room !== "object") return null;
  const otf = room?.ourFareDetails?.ourTotalFare;
  const pwccf = positive(otf?.priceWithCommissionAndCreditCardFee);
  if (pwccf != null) return pwccf;
  const pwc = positive(otf?.priceWithCommission);
  const hf = toNumberOrNull(otf?.handleFee) ?? 0;
  const cc = toNumberOrNull(otf?.creditCardFee) ?? 0;
  if (pwc != null) return round2(pwc + hf + cc);
  return positive(room?.selectedAddRoomPriceIls ?? room?.selected_add_room_price_ils);
}

/** Sum room PWCCF only — no supplements. */
export function sumPassengersInfoRoomsPwccfOnly(order) {
  const pi = order?.passengersinfo ?? order?.passengers_info;
  if (!Array.isArray(pi)) return { sum: null, roomsIncluded: 0 };
  let total = 0;
  let roomsIncluded = 0;
  for (const entry of pi) {
    const room = entry?.room && typeof entry.room === "object" ? entry.room : entry;
    const pwccf = readRoomPwccIls(room);
    if (pwccf != null) {
      total += pwccf;
      roomsIncluded += 1;
    }
  }
  if (roomsIncluded === 0) return { sum: null, roomsIncluded: 0 };
  return { sum: round2(total), roomsIncluded };
}

function legacyWithBaggageRefs(order, { roomsPwccfSum, supplementTotal }) {
  const refs = [];
  const seen = new Set();
  const add = (val) => {
    const n = positive(val);
    if (n == null) return;
    const key = round2(n);
    if (seen.has(key)) return;
    seen.add(key);
    refs.push(key);
  };

  const op = order?.ourprice ?? order?.ourPrice ?? {};
  const pwbf = positive(op.priceWithBaggageFare);
  add(pwbf);
  add(order?.customer_total ?? order?.customerTotal);

  const legacy = positive(extractLdsOrderFinalPriceIls(order));
  const fullComposed = round2(roomsPwccfSum + supplementTotal);
  if (legacy != null) {
    if (pwbf != null && roughlyEqual(legacy, pwbf)) add(legacy);
    else if (roughlyEqual(legacy, fullComposed)) add(legacy);
    else if (supplementTotal <= MONEY_TOL && roughlyEqual(legacy, roomsPwccfSum)) {
      add(legacy);
    }
  }

  const beUpdated =
    order?.lds_updated_order_total ?? order?.ldsUpdatedOrderTotal;
  add(beUpdated?.display_total_updated);
  add(
    order?.lds_partial_payment_display?.display_total ??
      order?.ldsPartialPaymentDisplay?.display_total
  );

  const paid = resolveLdsConfirmedPaidSumFromPayments(order).value;
  if (paid > MONEY_TOL && roughlyEqual(paid, roomsPwccfSum)) add(paid);

  return refs;
}

export function baggageAlreadyIncludedInRoomsPwccf(
  order,
  { roomsPwccfSum, supplementTotal }
) {
  if (supplementTotal <= MONEY_TOL) return false;
  const refs = legacyWithBaggageRefs(order, { roomsPwccfSum, supplementTotal });
  return refs.some((ref) => roughlyEqual(roomsPwccfSum, ref));
}

/**
 * Room fares + supplements, with double-count guard when baggage is already in PWCCF.
 */
export function resolvePassengersInfoRoomsDisplayTotal(order) {
  const { sum: roomsPwccfSum } = sumPassengersInfoRoomsPwccfOnly(order);
  if (roomsPwccfSum == null) return null;
  const supplementTotal = pickSupplementTotal(order) ?? 0;
  const beforeGuard = round2(roomsPwccfSum + supplementTotal);
  if (
    baggageAlreadyIncludedInRoomsPwccf(order, {
      roomsPwccfSum,
      supplementTotal,
    })
  ) {
    return roomsPwccfSum;
  }
  return beforeGuard;
}

function authoritativePaidRefs(order) {
  const op = order?.ourprice ?? order?.ourPrice ?? {};
  const refs = [
    positive(op.priceWithBaggageFare),
    positive(order?.customer_total ?? order?.customerTotal),
    positive(
      (order?.lds_updated_order_total ?? order?.ldsUpdatedOrderTotal)
        ?.display_total_updated
    ),
  ].filter((v) => v != null);
  const { sum: roomsPwccfSum } = sumPassengersInfoRoomsPwccfOnly(order);
  if (roomsPwccfSum != null) refs.push(roomsPwccfSum);
  return refs;
}

/** True when candidate is paid+supplement but paid already equals the full fare. */
function isPaidPlusSupplementDoubleCountTotal(candidate, order, supplementTotal) {
  const c = positive(candidate);
  if (c == null || supplementTotal <= MONEY_TOL) return false;
  const paid = resolveLdsConfirmedPaidSumFromPayments(order).value;
  if (paid <= MONEY_TOL || !roughlyEqual(c, round2(paid + supplementTotal))) {
    return false;
  }
  return authoritativePaidRefs(order).some((ref) => roughlyEqual(paid, ref));
}

/** True when candidate is rooms+supplement but rooms already include baggage. */
export function isInflatedBaggageDoubleCountTotal(candidate, order) {
  const c = positive(candidate);
  if (c == null) return false;
  const supplementTotal = pickSupplementTotal(order) ?? 0;
  if (supplementTotal <= MONEY_TOL) return false;

  if (isPaidPlusSupplementDoubleCountTotal(c, order, supplementTotal)) {
    return true;
  }

  const { sum: roomsPwccfSum } = sumPassengersInfoRoomsPwccfOnly(order);
  if (roomsPwccfSum == null) return false;
  if (!roughlyEqual(c, round2(roomsPwccfSum + supplementTotal))) return false;
  return baggageAlreadyIncludedInRoomsPwccf(order, {
    roomsPwccfSum,
    supplementTotal,
  });
}

function isDevRuntime() {
  if (typeof import.meta !== "undefined" && import.meta?.env?.DEV) return true;
  if (typeof process !== "undefined" && process?.env?.NODE_ENV) {
    return process.env.NODE_ENV !== "production";
  }
  return false;
}

export function emitBaggageDoubleCountGuardTrace(payload) {
  if (!isDevRuntime()) return;
  try {
    // eslint-disable-next-line no-console
    console.info(LDS_BAGGAGE_DOUBLE_COUNT_GUARD_TRACE, payload);
  } catch {
    /* ignore */
  }
}
